/**
 * © 2025 Grupo Salamander SAPI de CV
 * Author: Pablo Adoue Peralta
 *
 * UIButton component: Renders a button or icon button with optional tooltip, loading state, and icons via Material Symbols font.
 */

import UIButtonLabel from './UIButtonLabel'
import UIButtonIcon from './UIButtonIcon'
import { UIButtonTooltipWrapper } from './UIButtonTooltipWrapper'
import { DEFAULT_VARIANT, DEFAULT_COLOR } from './constants'
import type { TUIButtonProps } from './types'

// Re-export types for external use
export type {
  TUIButtonProps,
  TUIButtonIcon,
  TUIButtonLabel,
  TUIButtonVariant,
  TUIButtonIconValue,
} from './types'

// Re-export components for external use
export { UIButtonTooltipWrapper } from './UIButtonTooltipWrapper'

/**
 * Main UIButton component that renders either an icon button or a label button
 * based on the props provided. Automatically wraps with tooltip if specified.
 *
 * Features:
 * - Icon-only buttons with mandatory tooltip
 * - Label buttons with optional start/end icons
 * - Optional loading state
 * - Support for onClick and href (external link)
 * - Conditional rendering based on 'hidden' prop
 *
 * @param props - Button configuration props
 * @returns Rendered button component or null if hidden
 */
export function UIButton(props: TUIButtonProps) {
  const {
    hidden,
    label,
    icon,
    tooltip,
    variant = DEFAULT_VARIANT,
    color = DEFAULT_COLOR,
  } = props

  // Early return if button should be hidden
  if (hidden) {
    return null
  }

  // Render icon button with mandatory tooltip
  if (icon) {
    return (
      <UIButtonTooltipWrapper tooltip={tooltip}>
        <UIButtonIcon {...props} icon={icon} variant={variant} color={color} />
      </UIButtonTooltipWrapper>
    )
  }

  // Render label button with optional tooltip
  if (label) {
    return (
      <UIButtonTooltipWrapper tooltip={tooltip}>
        <UIButtonLabel
          {...props}
          label={label}
          variant={variant}
          color={color}
        />
      </UIButtonTooltipWrapper>
    )
  }

  // Fallback - should not reach here due to type system
  return null
}
